import axios from 'axios'
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import Countdown from 'react-countdown';
import ls from 'utils/ls';

function GetExamPage() {
  const { courseId, examId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const navState = location.state || {}
  const [data, setData] = useState(null)
  const [countDownTime, setCountDownTime] = useState(null)
  const [tests, setTests] = useState([])
  const [showConfirm, setShowConfirm] = useState(false)
  const [showResult, setShowResult] = useState(false)
  const [result, setResult] = useState(null)
  const [isFinished, setFinished] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isTimeUp, setIsTimeUp] = useState(false)
  const [loadError, setLoadError] = useState(false)

  useEffect(() => {
    getData(`exam/${examId}/start`, navState)
  }, [])

  useEffect(() => {
    const guard = (e) => { e.preventDefault(); e.returnValue = ''; }
    window.addEventListener('popstate', guard)
    window.addEventListener('beforeunload', guard)
    return () => {
      window.removeEventListener('popstate', guard)
      window.removeEventListener('beforeunload', guard)
    }
  }, [])

  const getData = (url, state = {}) => {
    ls.remove('tdt') // always start fresh
    setLoadError(false)
    const body = {}
    if (state.mode) body.mode = state.mode
    if (state.count) body.count = state.count
    axios({ method: 'POST', url, data: body }).then((res) => {
      setData(res.data)
      const expire = res.data?.expireDate ? new Date(res.data.expireDate).getTime() : null
      setCountDownTime(expire && !Number.isNaN(expire) ? expire : Date.now() + ((res.data.testMinute || res.data.duration || 60) * 60000))
      const newData = (res.data.questions || []).map(q => ({
        ...q,
        exam_question_answers: q.exam_question_answers || [],
        checkedId: [],
        isFilled: false,
        isCorrect: false
      }))
      ls.set('tdt', newData)
      setTests(newData)
    }).catch(() => setLoadError(true))
  }

  const handleChangeAnswer = (test, testIndex, answerIndex) => {
    const fixedData = [...tests]
    const answerId = fixedData[testIndex].exam_question_answers[answerIndex].id
    if (test.testType === 'Нэг сонголттой') {
      fixedData[testIndex].checkedId = [answerId]
      fixedData[testIndex].isFilled = true
    } else {
      const idx = fixedData[testIndex].checkedId.indexOf(answerId)
      if (idx === -1) {
        fixedData[testIndex].checkedId.push(answerId)
      } else {
        fixedData[testIndex].checkedId.splice(idx, 1)
      }
      fixedData[testIndex].isFilled = fixedData[testIndex].checkedId.length > 0
    }
    setTests([...fixedData])
    ls.set('tdt', fixedData)
  }

  const finishTest = () => {
    if (isSubmitting) return
    setIsSubmitting(true)
    const answers = tests.map(t => ({ testId: t.id, answers: [...t.checkedId] }))
    axios({ method: 'POST', url: `exam/${examId}/end`, data: answers })
      .then((res) => {
        setResult(res.data)
        setShowResult(true)
        setShowConfirm(false)
        ls.remove('tdt')
        setFinished(true)
      })
      .catch((err) => { setIsSubmitting(false); alert(err.response?.data?.message || 'Хариу илгээхэд алдаа гарлаа. Дахин оролдоно уу.') })
  }

  const answeredCount = tests.filter(t => t.isFilled).length
  const totalCount = tests.length
  const progressPct = totalCount > 0 ? Math.round((answeredCount / totalCount) * 100) : 0

  const timerRenderer = ({ hours, minutes, seconds, completed }) => {
    if (completed) {
      return <div className='text-center text-red-600 font-bold text-lg'>Хугацаа дууслаа</div>
    }
    const total = (hours * 60) + Number(minutes)
    const isLow = total < 5
    return (
      <div className={`text-center font-mono font-bold text-4xl tracking-widest mt-1 ${isLow ? 'text-red-500 animate-pulse' : 'text-slate-700'}`}>
        {hours > 0 && <span>{String(hours).padStart(2,'0')}:</span>}
        <span>{String(minutes).padStart(2,'0')}</span>
        <span className='text-2xl'>:{String(seconds).padStart(2,'0')}</span>
      </div>
    )
  }

  if (loadError) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <div className='text-center'>
          <p className='text-red-500 mb-4'>Шалгалт ачаалахад алдаа гарлаа.</p>
          <button onClick={() => getData(`exam/${examId}/start`, navState)} className='px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded'>Дахин оролдох</button>
        </div>
      </div>
    )
  }
  if (!data || tests.length === 0) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <div className='text-center'>
          <div className='w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4'/>
          <p className='text-slate-500'>Шалгалт ачааллаж байна...</p>
        </div>
      </div>
    )
  }

  return (
    <div className='min-h-screen bg-slate-50'>
      {/* Top bar */}
      <div className='bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm'>
        <div className='max-w-7xl mx-auto px-4 h-14 flex items-center justify-between'>
          <div className='font-semibold text-slate-800 text-lg truncate max-w-xs'>{data?.title}</div>
          <div className='flex items-center gap-4'>
            <div className='text-sm text-slate-500 hidden sm:block'>
              <span className='font-semibold text-blue-600'>{answeredCount}</span>/{totalCount} хариулсан
            </div>
            {!isFinished && (
              <button
                onClick={() => setShowConfirm(true)}
                className='bg-green-500 hover:bg-green-600 text-white text-sm font-medium px-4 py-1.5 rounded-lg transition-colors'
              >
                Дуусгах
              </button>
            )}
          </div>
        </div>
      </div>

      <div className='max-w-7xl mx-auto px-4 py-6 flex gap-6 items-start'>

        {/* Sidebar */}
        <div className='hidden md:flex w-72 shrink-0 flex-col sticky top-16' style={{ height: 'calc(100vh - 72px)' }}>
          <div className='bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col overflow-hidden h-full'>

            {/* Timer */}
            {countDownTime && !isFinished && (
              <div className='bg-slate-50 border-b border-slate-200 px-4 py-3 shrink-0'>
                <div className='text-xs text-slate-400 uppercase tracking-wide text-center mb-1'>Үлдсэн хугацаа</div>
                <Countdown date={countDownTime} renderer={timerRenderer} onComplete={() => { if (!isTimeUp) { setIsTimeUp(true); finishTest(); } }} />
              </div>
            )}

            {/* Progress */}
            <div className='px-4 py-3 border-b border-slate-200 shrink-0'>
              <div className='flex justify-between text-xs text-slate-500 mb-1'>
                <span>Хариулсан</span>
                <span className='font-semibold text-slate-700'>{answeredCount}/{totalCount}</span>
              </div>
              <div className='w-full bg-slate-200 rounded-full h-2'>
                <div
                  className='bg-blue-500 h-2 rounded-full transition-all duration-300'
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>

            {/* Question grid — fills remaining space, scrolls inside */}
            <div className='p-3 flex-1 overflow-y-auto'>
              <div className='grid grid-cols-6 gap-1'>
                {tests.map((test, i) => (
                  <a
                    key={i}
                    href={`#q-${test.id}`}
                    className={`
                      flex items-center justify-center h-8 rounded text-xs font-semibold transition-colors
                      ${test.isFilled
                        ? 'bg-blue-500 text-white hover:bg-blue-600'
                        : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}
                    `}
                  >
                    {i + 1}
                  </a>
                ))}
              </div>
            </div>

            {!isFinished && (
              <div className='p-3 border-t border-slate-200 shrink-0'>
                <button
                  onClick={() => setShowConfirm(true)}
                  className='w-full bg-green-500 hover:bg-green-600 text-white font-medium py-2 rounded-lg transition-colors text-sm'
                >
                  Дуусгах
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Questions */}
        <div className='flex-1 min-w-0 space-y-4'>
          {tests.map((test, i) => (
            <div
              key={i}
              id={`q-${test.id}`}
              className={`bg-white rounded-xl border shadow-sm transition-all scroll-mt-20 ${test.isFilled ? 'border-blue-200' : 'border-slate-200'}`}
            >
              <div className='p-4 border-b border-slate-100 flex gap-3 items-start'>
                <span className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${test.isFilled ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-500'}`}>
                  {i + 1}
                </span>
                <div
                  className='text-slate-800 leading-relaxed pt-1 flex-1'
                  dangerouslySetInnerHTML={{ __html: test?.question }}
                />
              </div>
              <div className='p-4 space-y-2'>
                {(test.exam_question_answers || []).map((answer, idx) => {
                  const isChecked = test.checkedId.includes(answer.id)
                  const inputType = test.testType === 'Нэг сонголттой' ? 'radio' : 'checkbox'
                  return (
                    <label
                      key={idx}
                      className={`
                        flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all border
                        ${isChecked
                          ? 'bg-blue-50 border-blue-400 text-blue-800'
                          : 'bg-slate-50 border-slate-200 hover:border-blue-300 hover:bg-blue-50 text-slate-700'}
                      `}
                    >
                      <input
                        id={answer.id}
                        name={`q-${test.id}`}
                        checked={isChecked}
                        type={inputType}
                        onChange={() => handleChangeAnswer(test, i, idx)}
                        className='mt-0.5 accent-blue-500 shrink-0'
                      />
                      <span className='leading-snug text-sm'>{answer.answer}</span>
                    </label>
                  )
                })}
              </div>
            </div>
          ))}

          {!isFinished && (
            <button
              onClick={() => setShowConfirm(true)}
              className='w-full bg-green-500 hover:bg-green-600 text-white font-medium py-3 rounded-xl transition-colors text-base'
            >
              Шалгалт дуусгах
            </button>
          )}
        </div>
      </div>

      {/* Confirm modal */}
      {showConfirm && (
        <div className='fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4'>
          <div className='bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm'>
            <div className='text-center mb-4'>
              <div className='w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-3'>
                <svg className='w-7 h-7 text-amber-500' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z'/>
                </svg>
              </div>
              <h3 className='text-lg font-semibold text-slate-800'>Шалгалт дуусгах уу?</h3>
              <p className='text-slate-500 text-sm mt-1'>
                {totalCount - answeredCount > 0
                  ? <span className='text-orange-500 font-medium'>{totalCount - answeredCount} асуулт</span>
                  : <span className='text-green-600 font-medium'>Бүх асуулт</span>}
                {totalCount - answeredCount > 0 ? ' хариулаагүй байна.' : ' хариуллаа.'}
              </p>
            </div>
            <div className='flex gap-3'>
              <button onClick={() => setShowConfirm(false)} className='flex-1 py-2.5 rounded-xl border border-slate-300 text-slate-600 hover:bg-slate-50 text-sm font-medium'>
                Буцах
              </button>
              <button
                onClick={finishTest}
                disabled={isSubmitting}
                className='flex-1 py-2.5 rounded-xl bg-green-500 hover:bg-green-600 text-white text-sm font-medium disabled:opacity-60'
              >
                {isSubmitting ? 'Илгээж байна...' : 'Дуусгах'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Result modal */}
      {showResult && result && (
        <div className='fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4'>
          <div className='bg-white rounded-2xl shadow-2xl p-6 w-full max-w-lg'>
            <div className='text-center mb-6'>
              <div className={`w-24 h-24 rounded-full flex flex-col items-center justify-center mx-auto mb-4 ${result.status === 'Тэнцсэн' ? 'bg-green-100' : 'bg-red-100'}`}>
                {result.status === 'Тэнцсэн'
                  ? <svg className='w-12 h-12 text-green-500' fill='none' viewBox='0 0 24 24' stroke='currentColor'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7'/></svg>
                  : <svg className='w-12 h-12 text-red-500' fill='none' viewBox='0 0 24 24' stroke='currentColor'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12'/></svg>
                }
              </div>
              <div className={`text-3xl font-bold mb-1 ${result.status === 'Тэнцсэн' ? 'text-green-600' : 'text-red-500'}`}>
                {result.status}
              </div>
              <div className='text-5xl font-black text-slate-800 mb-1'>{result.score}<span className='text-2xl text-slate-400'>%</span></div>
              <div className='text-slate-500 text-sm'>
                {result.correctCount}/{result.total} зөв хариулт
              </div>
            </div>

            <div className='mb-5'>
              <div className='text-xs text-slate-400 uppercase tracking-wide mb-2 text-center'>Асуултын дүн</div>
              <div className='grid grid-cols-10 gap-1'>
                {result.result.map((r, i) => (
                  <div
                    key={i}
                    title={`${i + 1}-р асуулт`}
                    className={`h-7 rounded flex items-center justify-center text-xs font-semibold text-white
                      ${r.isCorrect === 1 ? 'bg-green-400' : 'bg-red-400'}`}
                  >
                    {i + 1}
                  </div>
                ))}
              </div>
            </div>

            <div className='flex gap-3'>
              <button
                onClick={() => navigate(-1)}
                className='flex-1 py-3 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-medium transition-colors'
              >
                Буцах
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default GetExamPage
